import React, { useEffect, useRef, useState } from 'react'

import { ConfigBudget, getConfigBudget } from '../services/api'

/**
 * How much of the Intune install command this template already spends, while it is being written.
 *
 * The configuration travels inline on the install command line, which Intune caps at 1024
 * characters. Saving already refuses a template that will not fit — but only once the author has
 * finished and pressed save, at which point the field to shorten is behind them. The figure is the
 * same measurement, offered early enough to act on.
 *
 * MEASURED BY THE SERVER, not here. The cost is of the rendered, slimmed, wire-coded sidecar, and
 * the tenant's branding is part of it: the publisher renders a product name into the same payload.
 * A second implementation in the browser would drift from the one that decides, and the moment it
 * did, the author would be told something untrue in whichever direction it differed.
 *
 * QUIET UNTIL IT MATTERS. A template with room to spare is the normal case and needs no meter, so
 * nothing is drawn until the draft is within reach of the limit. A gauge that is always present
 * either becomes furniture or reads as a warning about a problem nobody has.
 */

/** Below this share of the budget there is nothing worth saying. */
const SPEAK_AT = 0.75

export const ConfigBudgetMeter: React.FC<{
  /** The draft, in the shape the server measures. Re-measured when it changes. */
  config: Record<string, any>
  /** Debounce, ms. Typing must not put a request on every keystroke. */
  delay?: number
}> = ({ config, delay = 400 }) => {
  const [budget, setBudget] = useState<ConfigBudget | null>(null)
  // The draft as JSON, so the effect re-runs on a CHANGE of content rather than on every render —
  // the object identity changes constantly while an unrelated field is edited.
  const key = JSON.stringify(config ?? {})
  // Guards a late response from overwriting a newer one: the requests are debounced, not ordered.
  const latest = useRef(0)

  useEffect(() => {
    const seq = ++latest.current
    const timer = setTimeout(() => {
      getConfigBudget(config)
        .then((b) => {
          if (seq === latest.current) setBudget(b)
        })
        .catch(() => {
          // The figure is an aid, not a gate — saving still refuses what will not fit. Showing a
          // stale number would be worse than showing none.
          if (seq === latest.current) setBudget(null)
        })
    }, delay)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, delay])

  if (!budget || budget.used == null || budget.remaining == null) return null
  const share = budget.used / budget.budget
  if (budget.fits && share < SPEAK_AT) return null

  const over = !budget.fits
  return (
    <div
      role="status"
      className={`mt-1.5 rounded-lg border px-3 py-2 text-xs ${
        over
          ? 'border-red-500/30 bg-red-500/[0.07] text-red-400'
          : 'border-amber-500/30 bg-amber-500/[0.07] text-amber-500'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span>
          {over
            ? `${budget.used - budget.budget} characters over the install command limit`
            : `${budget.remaining} characters left of the install command limit`}
        </span>
        <span className="font-mono text-[11px] opacity-80">
          {budget.used}/{budget.budget}
        </span>
      </div>
      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-black/20">
        <div
          className={`h-full ${over ? 'bg-red-400' : 'bg-amber-400'}`}
          style={{ width: `${Math.min(100, Math.round(share * 100))}%` }}
        />
      </div>
      {over && (
        <p className="mt-1.5">
          This cannot be saved until it fits. The company name is usually the only field long enough
          to matter — everything else is a fixed-size choice.
        </p>
      )}
    </div>
  )
}

export default ConfigBudgetMeter
